import { forwardRef } from 'react';
import { Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LanguageProvider } from '@/hooks/useLanguage';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import ScrollToTop from '@/components/ScrollToTop';
import RedirectHandler from '@/components/RedirectHandler';
import ClientOnly from '@/components/ClientOnly';
import ChatheroChatbot from '@/components/ChatheroChatbot';

const queryClient = new QueryClient();

// NOTE: Do NOT wrap the tree in <HelmetProvider> here.
// vite-react-ssg already provides its own HelmetProvider with the
// helmetContext it uses to serialize <head> into the prerendered HTML.
// A nested HelmetProvider would create a separate context and swallow
// all Helmet writes, causing every prerendered page to share the static
// index.html head (wrong title / canonical / og tags).
const RootLayout = forwardRef<HTMLDivElement>((_props, ref) => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          {/* Render toast providers only on client to prevent SSG hydration mismatches */}
          <ClientOnly>
            <Toaster />
            <Sonner />
            <RedirectHandler />
            <ScrollToTop />
            <ChatheroChatbot />
          </ClientOnly>
          <div ref={ref}>
            <Outlet />
          </div>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
});

RootLayout.displayName = 'RootLayout';

export default RootLayout;
